import {announce} from '@react-aria/live-announcer'
import Big from 'big.js'

import useDiscountAmount from '@/hooks/useDiscountAmount'
import useFlightDetailsQuery from '@/lib/flight/hooks/useFlightDetails'
import {useCurrencySymbol} from '@/lib/locale/country'
import useCashback from '@/lib/payment/hooks/useCashback'
import useCoupon from '@/lib/payment/hooks/useCoupon'
import {useRoundUp} from '@/lib/payment/store'

export default function useFlightSummary(
  flightId: string,
  option: number,
  isRewardsApplied: boolean,
  coupon: string,
  setCoupon?: (coupon: string) => void | Promise<void>,
) {
  const {data: flightDetails, isLoading} = useFlightDetailsQuery(flightId)
  const currencySymbol = useCurrencySymbol()
  const latestCurrencySymbol = useLatest(currencySymbol)
  const [roundUp] = useRoundUp()

  const subtotal = flightDetails
    ? Big(flightDetails.booking_options[Number(option)]?.together?.price ?? 0)
    : Big(0)

  const couponRef = useRef<string>('')
  const [couponIsDirty, setCouponIsDirty] = useState(false)
  const applyCoupon = useCallback(() => {
    void setCoupon?.(couponRef.current)
  }, [setCoupon])

  const handleCouponChange = useCallback(
    (value: string) => {
      couponRef.current = value
      if (value === '') {
        setCouponIsDirty(false)
      } else {
        setCouponIsDirty(true)
      }
      void setCoupon?.('')
    },
    [setCoupon],
  )

  const {
    data: couponInfo,
    error: couponError,
    isFetching: isCouponLoading,
  } = useCoupon(coupon, !isRewardsApplied)

  useEffect(
    function announceCouponSuccess() {
      if (couponInfo) {
        announce(`Coupon applied: -${latestCurrencySymbol.current}${couponInfo.amount}`)
      }
    },
    [couponInfo, couponInfo?.code],
  )

  useEffect(
    function announceCouponError() {
      if (couponError === null) return
      announce(couponError.message)
    },
    [couponError, couponError?.message],
  )

  const roundUpAmount = subtotal.round(0, Big.roundUp).minus(subtotal)

  const totalAfterRoundUp = roundUp ? subtotal.plus(roundUpAmount) : subtotal

  const {data: {discount: discountValue} = {discount: 0}} = useDiscountAmount(totalAfterRoundUp)

  const derivedDiscountValue = (() => {
    if (isRewardsApplied) {
      return discountValue
    }

    if (couponInfo) {
      return couponInfo.amount
    }

    return 0
  })()

  let total = totalAfterRoundUp.minus(derivedDiscountValue)
  if (total.lt(0)) {
    total = Big(0)
  }

  const {data: cashback} = useCashback(total)

  return {
    flightDetails,
    isLoading,
    cashback,
    total,
    discountValue,
    derivedDiscountValue,
    subtotal,
    applyCoupon,
    couponError,
    handleCouponChange,
    couponInfo,
    isCouponLoading,
    coupon,
    couponIsDirty,
    roundUpAmount,
    totalAfterRoundUp,
  }
}
